/**
 * Сравнение версий бюджета (БМ-01): показанная версия против выбранной — суммы разделов, статей и вариантов
 * «было → стало», смена выбранного варианта, план оплат по неделям.
 */
import type { BudgetCompareView, BudgetVersion } from '@epc/contracts';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../../lib/api';
import { date, tenge } from '../../lib/format';
import { Badge, ErrorBox, Spinner } from '../../ui/feedback';
import { Field, Select } from '../../ui/form';
import { Panel } from '../../ui/layout';

const signed = (v: number | null) =>
  v === null ? '—' : `${v > 0 ? '+' : v < 0 ? '−' : ''}${tenge(Math.abs(v))}`;
const statusLabel = { draft: 'черновик', approved: 'утверждена', superseded: 'заменена' } as const;

function Result({ c }: { c: BudgetCompareView }) {
  const t = c.totals;
  return (
    <div className="mt-4 flex flex-col gap-4 text-[14px]">
      <p className="num">
        «{c.from.name}» → «{c.to.name}». Бюджет: было {tenge(t.was)} → стало {tenge(t.became)}{' '}
        <b>({signed(t.delta)})</b>; по неделям {tenge(t.distributedWas)} → {tenge(t.distributedBecame)}. Без
        изменений строк: {c.linesSame}.
      </p>
      {c.lines.length ? (
        <ul className="flex flex-col gap-2">
          {c.lines.map((l, i) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: у строк разных версий нет общего номера
            <li key={i} className="rounded-md border border-border px-3 py-2">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-medium">
                  {l.code ? `${l.code}. ` : ''}
                  {l.name}
                </span>
                {l.was === null ? <Badge tone="success">добавлена</Badge> : null}
                {l.became === null ? <Badge tone="danger">убрана</Badge> : null}
                {l.selectedWas !== null && l.selectedBecame !== null && l.selectedWas !== l.selectedBecame ? (
                  <Badge tone="warning">{l.selectedBecame ? 'вариант выбран' : 'выбор снят'}</Badge>
                ) : null}
              </div>
              {l.parentName ? <p className="text-ink-2">в разделе {l.parentName}</p> : null}
              <p className="num mt-1">
                было {tenge(l.was)} → стало {tenge(l.became)} <b>({signed(l.delta)})</b>
              </p>
              {l.effectiveWas !== null || l.effectiveBecame !== null ? (
                <p className="num mt-1">
                  в бюджете с выбранным вариантом и изменениями: {tenge(l.effectiveWas)} →{' '}
                  {tenge(l.effectiveBecame)}
                </p>
              ) : null}
              {l.weeks.length ? (
                <p className="mt-1 text-ink-2">
                  План оплат изменён: {l.weeks.map((w) => date(w)).join(', ')}
                </p>
              ) : null}
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-ink-2">Строки версий совпадают до тенге.</p>
      )}
      {c.weeks.length ? (
        <div>
          <p className="mb-2 font-medium">План оплат по неделям</p>
          <ul className="flex flex-col gap-1">
            {c.weeks.map((w) => (
              <li key={w.start} className="num">
                неделя с {date(w.start)}: {tenge(w.was)} → {tenge(w.became)} <b>({signed(w.delta)})</b>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

export function CompareVersionsPanel({
  current,
  versions,
}: {
  current: BudgetVersion;
  versions: BudgetVersion[];
}) {
  const others = versions.filter((v) => v.id !== current.id);
  const [withId, setWithId] = useState('');
  // Выбор сбрасывается, если версия исчезла из списка или стала показанной
  const target = others.some((v) => v.id === withId) ? withId : '';
  const q = useQuery({
    queryKey: ['budget', 'compare', current.id, target],
    queryFn: () =>
      api<BudgetCompareView>(`/budget-versions/${current.id}/compare`, { query: { with: target } }),
    enabled: Boolean(target),
  });
  if (!others.length) return null;
  return (
    <Panel title="Сравнение версий" meta="было — более ранняя версия, стало — более поздняя">
      <Field label="Сравнить с версией" htmlFor="budget-compare-with">
        <Select
          id="budget-compare-with"
          className="min-h-11"
          value={target}
          onChange={(e) => setWithId(e.target.value)}
        >
          <option value="">— выберите —</option>
          {others.map((v) => (
            <option key={v.id} value={v.id}>
              {v.name} — {statusLabel[v.status]}
            </option>
          ))}
        </Select>
      </Field>
      {target ? (
        q.isPending ? (
          <Spinner />
        ) : q.isError ? (
          <ErrorBox error={q.error} />
        ) : (
          <Result c={q.data} />
        )
      ) : null}
    </Panel>
  );
}
